import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import dashboardService from '../services/dashboardService';
import {
  activityView, attentionItems, departmentCards, greeting, isToday, nowKpis, periodKpis, pipelineStages, rangeFor, rangeLabel, unavailableSections,
} from '../utils/dashboardMetrics';
import {
  ActivityPanel, AttentionPanel, DepartmentCard, Icon, KpiCard, PipelineStrip, RangePicker, RecentRegistrations, SystemStrip, TrendPanel,
} from './dashboard/DashboardParts';
import './dashboard/Dashboard.css';

export const Dashboard = () => {
  const { user, can } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.roleName?.toUpperCase() === 'ADMINISTRATOR' || user?.roleId === 1;
  const [selectedCentreId, setSelectedCentreId] = useState(user?.centreId || null);
  const [range, setRange] = useState(() => rangeFor('today'));   // opens on today; every period number follows it
  const [autoRefresh, setAutoRefresh] = useState('0');           // seconds as text; '0' = off
  const [loading, setLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [stats, setStats] = useState(null);                      // what is open right now (queues, dues)
  const [period, setPeriod] = useState(null);                    // what happened in the chosen range
  const [periodFailed, setPeriodFailed] = useState(false);
  const [activity, setActivity] = useState(null);
  const [activityFailed, setActivityFailed] = useState(false);
  const statsRequest = useRef(0);
  const rangeRequest = useRef(0);

  // The centres this user may look at (from the workspaces they were given).
  const centres = useMemo(() => {
    const seen = new Set();
    const list = [];
    (user?.availableWorkspaces || []).forEach((w) => {
      if (w.centreId && !seen.has(w.centreId)) { seen.add(w.centreId); list.push({ centreId: w.centreId, centreName: w.centreName, centreCode: w.centreCode }); }
    });
    if (list.length === 0 && user?.centreId) list.push({ centreId: user.centreId, centreName: user.centreName, centreCode: user.centreCode });
    return list;
  }, [user]);
  const activeCentre = centres.find((c) => c.centreId === Number(selectedCentreId));
  const centreLabel = activeCentre ? activeCentre.centreName : (selectedCentreId ? user?.centreName : 'All centres');

  // Only the newest answer of each kind is kept, so a quick change of centre or dates never shows an older answer.
  const loadStats = useCallback(async () => {
    const id = ++statsRequest.current;
    setLoading(true);
    try {
      const data = await dashboardService.loadAllDashboardData(selectedCentreId, { includeSecurity: isAdmin });
      if (id !== statsRequest.current) return;
      setStats(data);
      setLastRefreshedAt(new Date());
    } catch (err) {
      if (id === statsRequest.current) console.error('Error fetching dashboard data:', err);
    } finally {
      if (id === statsRequest.current) setLoading(false);
    }
  }, [selectedCentreId, isAdmin]);

  // Everything that depends on the dates: the numbers of the period, the days of it, and what people did.
  const loadRange = useCallback(async () => {
    const id = ++rangeRequest.current;
    const args = { from: range.from, to: range.to, centreId: selectedCentreId };
    const [summary, people] = await Promise.all([dashboardService.getPeriodSummary(args), dashboardService.getStaffActivity(args)]);
    if (id !== rangeRequest.current) return;
    setPeriod(summary?.success ? summary.data : null);
    setPeriodFailed(!summary?.success);
    setActivity(people?.success ? people.data : null);
    setActivityFailed(!people?.success);
  }, [selectedCentreId, range]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRange(); }, [loadRange]);

  const refreshAll = useCallback(() => { loadStats(); loadRange(); }, [loadStats, loadRange]);

  // Auto refresh: not while the tab is hidden, and one more refresh as soon as it is shown again.
  useEffect(() => {
    const seconds = parseInt(autoRefresh, 10);
    if (!(seconds > 0)) return undefined;
    const tick = () => { if (!document.hidden) refreshAll(); };
    const id = setInterval(tick, seconds * 1000);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, [autoRefresh, refreshAll]);

  const chooseRange = useCallback((key, custom) => setRange(rangeFor(key, new Date(), custom)), []);

  const canView = useCallback((screen) => can(screen, 'VIEW'), [can]);
  const open = useCallback((route, params) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    navigate(`${route}${query}`);
  }, [navigate]);

  const onToday = isToday(range);
  const rangeText = rangeLabel(range);
  const kpis = useMemo(() => periodKpis(stats, period, onToday), [stats, period, onToday]);
  const openNow = useMemo(() => nowKpis(stats), [stats]);
  const stages = useMemo(() => pipelineStages(stats, period, onToday), [stats, period, onToday]);
  const attention = useMemo(() => attentionItems(stats), [stats]);
  const departments = useMemo(() => departmentCards(stats), [stats]);
  const activityPanelView = useMemo(() => activityView(activity), [activity]);
  const missing = useMemo(() => {
    const list = stats ? unavailableSections(stats, { includeSecurity: isAdmin }) : [];
    return periodFailed ? [...list, 'Period summary (Billed, Results Entered and the daily trend need it)'] : list;
  }, [stats, isAdmin, periodFailed]);

  const waiting = loading && !stats;
  const name = user?.employeeName || user?.username || '';
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="ld-page">
      <header className="ld-top">
        <div className="ld-hello">
          <h1>{greeting()}{name ? `, ${name.split(' ')[0]}` : ''}</h1>
          <p>{today} · {centreLabel}</p>
        </div>
        <div className="ld-controls">
          <label className="ld-select" title="Centre">
            <span className="ld-sr">Centre</span>
            <select value={selectedCentreId || ''} onChange={(e) => setSelectedCentreId(e.target.value ? Number(e.target.value) : null)}>
              {isAdmin && <option value="">All centres (consolidated)</option>}
              {centres.map((c) => <option key={c.centreId} value={c.centreId}>{c.centreName}{c.centreCode ? ` (${c.centreCode})` : ''}</option>)}
            </select>
          </label>
          <label className="ld-select" title="Refresh automatically">
            <span className="ld-sr">Auto refresh</span>
            <select value={autoRefresh} onChange={(e) => setAutoRefresh(e.target.value)}>
              <option value="0">Manual refresh</option>
              <option value="30">Every 30 s</option>
              <option value="60">Every 60 s</option>
            </select>
          </label>
          <button type="button" className="ld-refresh" onClick={refreshAll} disabled={loading} title="Refresh now">
            <span className={loading ? 'spin' : ''}><Icon name="refresh" size={15} /></span>
            {loading ? 'Syncing' : 'Refresh'}
          </button>
          <span className="ld-synced" title="Last synchronised">{lastRefreshedAt ? lastRefreshedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</span>
        </div>
      </header>

      <RangePicker range={range} onChange={chooseRange} />

      {missing.length > 0 && (
        <div className="ld-notice" role="status">
          <span>Could not load: {missing.join(', ')}. Those numbers show “—”.</span>
          <button type="button" className="ld-link" onClick={refreshAll}>Try again</button>
        </div>
      )}

      <section aria-label="In the selected period">
        <h2 className="ld-heading">In the selected period · {rangeText}</h2>
        <div className="ld-kpis">
          {kpis.map((k) => <KpiCard key={k.key} kpi={k} loading={waiting && !period} allowed={canView(k.screen)} onOpen={open} />)}
        </div>
      </section>

      <section aria-label="Open right now">
        <h2 className="ld-heading">Open right now · does not change with the dates</h2>
        <div className="ld-kpis">
          {openNow.map((k) => <KpiCard key={k.key} kpi={k} loading={waiting} allowed={canView(k.screen)} onOpen={open} />)}
        </div>
      </section>

      <TrendPanel period={period} label={rangeText} />

      <section aria-label="Lab workflow">
        <h2 className="ld-heading">Lab workflow · waiting now, done in the period</h2>
        <PipelineStrip stages={stages} loading={waiting} can={canView} onOpen={open} />
      </section>

      <div className="ld-columns">
        <div className="ld-side">
          <AttentionPanel items={attention} loading={waiting} can={canView} onOpen={open} anyUnavailable={missing.length > 0} />
          <ActivityPanel view={activityPanelView} loading={!activity && !activityFailed} failed={activityFailed} rangeText={rangeText} />
        </div>
        <div className="ld-dept-grid">
          {departments.map((d) => <DepartmentCard key={d.key} card={d} loading={waiting} allowed={canView(d.screen)} onOpen={open} />)}
        </div>
      </div>

      {canView('REGISTRATION') && (
        <RecentRegistrations items={stats?.recentRegistrations?.items} loading={waiting} canBill={canView('SETTLEMENT')} onOpen={open} />
      )}

      {isAdmin && <SystemStrip security={stats?.security} health={stats?.health} />}
    </div>
  );
};

export default Dashboard;
